import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Button, { ButtonTypes } from 'components/common/button';
import CustomerProfile from 'models/customer_profile';
import ExternalCustomerProfile from 'models/external_customer_profile';
import { ExternalLookupResultAttributeDef } from 'models/customer_profile_def';
import ExternalProfileRow from './external_profile_row';
import { formatValue } from 'components/customer/summary/custom_attributes/visible_custom_attribute';
import GladlyProfileRow from './gladly_profile_row';
import LoadingSpinner from 'components/lib/loading_spinner';
import ResultsHeader from './results_header';

const SHOW_OVERFLOW_FADES_DEBOUNCE_MS = 100;
const LINK_ACTION = 'Link';
const REPLACE_LINK_ACTION = 'Replace Link';

export default class ExternalCustomerLookupSearchResults extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      selectedIndex: -1,
    };

    _.bindAll(this, ['handleCancelClick', 'handleLinkClick', 'showOverflowFades']);

    this.showOverflowFades();
    this.showOverflowFades = _.debounce(this.showOverflowFades, SHOW_OVERFLOW_FADES_DEBOUNCE_MS);
  }

  componentDidUpdate() {
    this.showOverflowFades();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.lookupResultProfiles !== nextProps.lookupResultProfiles) {
      this.setState({ selectedIndex: -1 });
    }
  }

  render() {
    if (
      !this.props.isExternalLookupPending &&
      this.props.lookupResultProfiles == null &&
      !this.props.lookupSuggestions.length
    ) {
      return this.renderEmptySearch();
    }

    const tableClassNames = classnames('externalCustomerLookupSearchResults-table', {
      'externalCustomerLookupSearchResults-table-fadeLeft': this.state.shouldFadeLeft,
      'externalCustomerLookupSearchResults-table-fadeRight': this.state.shouldFadeRight,
    });
    const scrollerClassNames = classnames('externalCustomerLookupSearchResults-table-scroller', {
      'externalCustomerLookupSearchResults-table-scroller-noResults':
        this.hasNoResults() || this.props.isExternalLookupPending,
    });

    return (
      <div className="externalCustomerLookupSearchResults">
        <ResultsHeader
          isLookupPending={this.props.isExternalLookupPending}
          resultsCount={_.get(this.props.lookupResultProfiles, 'length', 0)}
          shouldShowSuggestions={this.shouldShowSuggestions()}
          suggestionsCount={this.props.lookupSuggestions.length}
        />
        <div className="externalCustomerLookupSearchResults-table-wrapper">
          <div className="externalCustomerLookupSearchResults-table-columnPinner">
            <div className={scrollerClassNames} onScroll={this.showOverflowFades} ref={node => (this.scroller = node)}>
              <table className={tableClassNames} ref={node => (this.table = node)}>
                {this.renderTableHeader()}
                {this.renderTableBody()}
              </table>
            </div>
            {this.renderEmptyResults()}
          </div>
        </div>
        {this.renderLookupActions()}
      </div>
    );
  }

  renderTableHeader() {
    return (
      <thead>
        <tr>
          <th className="externalCustomerLookupSearchResults-table-sourceCell" key="externalCustomerLookup-cell1" />
          <th key="externalCustomerLookup-cell2" />
          {_.map(this.props.lookupResultAttributes, attr => (
            <th key={attr.attr}>{attr.label}</th>
          ))}
        </tr>
      </thead>
    );
  }

  renderTableBody() {
    return (
      <tbody>
        <GladlyProfileRow profileRow={getProfileRow(this.props.lookupResultAttributes, this.props.profile)} />
        {_.map(this.getResultProfiles(), (externalProfile, index) => {
          const profileRow = getProfileRow(this.props.lookupResultAttributes, externalProfile);
          return (
            <ExternalProfileRow
              externalSystemName={this.props.externalSystemName}
              isSelected={index === this.state.selectedIndex}
              key={`externalCustomer-${index}`}
              onClick={this.handleClickRow.bind(this, index)}
              profileRow={profileRow}
              shouldDisplaySource={index === 0}
            />
          );
        })}
      </tbody>
    );
  }

  renderLookupActions() {
    return (
      <div className="externalCustomerLookupSearchResults-actions">
        <Button
          blurOnClick
          buttonType={ButtonTypes.TEXT}
          className="externalCustomerLookupSearchResults-cancelButton"
          onClick={this.handleCancelClick}
          onMouseDown={evt => evt.preventDefault()}
        >
          Cancel
        </Button>
        <Button
          blurOnClick
          buttonType={ButtonTypes.PRIMARY}
          className="externalCustomerLookupSearchResults-submitButton"
          disabled={this.state.selectedIndex < 0}
          onClick={this.handleLinkClick}
          onMouseDown={evt => evt.preventDefault()}
        >
          {this.props.isAlreadyLinked ? REPLACE_LINK_ACTION : LINK_ACTION}
        </Button>
      </div>
    );
  }

  renderEmptySearch() {
    return <div className="externalCustomerLookupSearchResults-emptySearch">Enter search fields for results</div>;
  }

  renderEmptyResults() {
    if (!this.hasNoResults() && !this.props.isExternalLookupPending) {
      return null;
    }

    let content = 'No results found for search';
    if (this.props.isExternalLookupPending) {
      content = <LoadingSpinner containerClassName="externalCustomerLookupSearchResults-table-loadingSpinner" />;
    }

    return (
      <div className="externalCustomerLookupSearchResults-table-noResults">
        <div className="externalCustomerLookupSearchResults-table-noResults-source">
          {this.props.externalSystemName || 'External'}
        </div>
        <div className="externalCustomerLookupSearchResults-table-noResults-content">{content}</div>
      </div>
    );
  }

  handleCancelClick() {
    this.props.onCancel();
  }

  handleClickRow(index) {
    this.setState(({ selectedIndex }) => ({ selectedIndex: selectedIndex === index ? -1 : index }));
  }

  handleLinkClick() {
    if (this.state.selectedIndex >= 0) {
      this.props.onLink(this.getResultProfiles()[this.state.selectedIndex], this.state.selectedIndex);
    }
  }

  getResultProfiles() {
    return (
      (!this.props.isExternalLookupPending && (this.props.lookupResultProfiles || this.props.lookupSuggestions)) || []
    );
  }

  hasNoResults() {
    return this.props.lookupResultProfiles && !this.props.lookupResultProfiles.length;
  }

  shouldShowSuggestions() {
    return !!this.props.lookupSuggestions.length && !this.props.lookupResultProfiles;
  }

  showOverflowFades() {
    if (!this.table || !this.scroller) {
      return;
    }

    const tableRect = this.table.getBoundingClientRect();
    const scrollerRect = this.scroller.getBoundingClientRect();

    const maxScroll = tableRect.width - scrollerRect.width;

    // No overflow -> don't worry about fades
    if (!maxScroll) {
      return;
    }

    // Note that setting state isn't as performant as setting the styles directly - but we don't need fantastic speeds on this page
    this.setState({
      shouldFadeLeft: this.scroller.scrollLeft > 0,
      shouldFadeRight: this.scroller.scrollLeft < maxScroll,
    });
  }
}

ExternalCustomerLookupSearchResults.propTypes = {
  externalSystemName: PropTypes.string,
  isAlreadyLinked: PropTypes.bool,
  isExternalLookupPending: PropTypes.bool,
  lookupResultAttributes: PropTypes.arrayOf(PropTypes.instanceOf(ExternalLookupResultAttributeDef)).isRequired,
  lookupResultProfiles: PropTypes.arrayOf(PropTypes.instanceOf(ExternalCustomerProfile)),
  lookupSuggestions: PropTypes.arrayOf(PropTypes.instanceOf(ExternalCustomerProfile)).isRequired,
  onCancel: PropTypes.func.isRequired,
  onLink: PropTypes.func.isRequired,
  profile: PropTypes.instanceOf(CustomerProfile).isRequired,
};

function getProfileRow(lookupResultAttributes, profile) {
  return _.map(lookupResultAttributes, attr => {
    if (attr.attr === 'email') {
      return (
        _.get(
          _.find(profile.emails, email => !!email.primary),
          'original'
        ) ||
        _.get(profile.emails[0], 'original') ||
        ''
      );
    } else if (attr.attr === 'phone') {
      return (
        _.get(
          _.find(profile.phones, phone => !!phone.primary),
          'original'
        ) ||
        _.get(profile.phones[0], 'original') ||
        ''
      );
    } else if (profile.hasOwnProperty(attr.attr)) {
      return profile[attr.attr] || '';
    } else if (profile.customAttributes && profile.customAttributes.hasOwnProperty(attr.attr)) {
      const value = profile.customAttributes[attr.attr];
      return (value && formatValue(value, attr.type)) || '';
    }
    return '';
  });
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/external_customer_lookup/external_customer_lookup_search_results.jsx